// @flow
import * as periods from '~/core/constants/periods';
import * as React from 'react';

import { injectIntl } from 'react-intl';

import { barColourModes } from '~/components/widgets/bar-graph/constants';
import BarGraph from '~/components/widgets/bar-graph';
import { findIndex, first, last, range } from 'lodash/fp';
import { getRangeColourStrings } from '~/core/utils/colour';
import messages from './intl';
import moment from 'moment-timezone';

const { YDOMAIN } = barColourModes;

/************************
 *   Helper functions   *
 ************************/

const _injectXLabels = (graphData, resolution, timezone) =>
    graphData.map(entry => {
        const entryDate = moment(entry.date).tz(timezone);
        const labelMap = {
            [periods.TODAY.resolution]: entryDate.format('HH:mm'),
            [periods.THIS_WEEK.resolution]: entryDate.format('ddd MMM DD'),
            [periods.THIS_MONTH.resolution]: entryDate.format('MMM DD'),
            [periods.THIS_YEAR.resolution]: entryDate.format('MMM')
        };
        return { ...entry, labelX: labelMap[resolution] };
    });

const minDayLength = 47;
const minWeekLength = 7;
const minYearLength = 12;
const getMinMonthLength = d =>
    moment(d)
        .endOf('month')
        .date();

const incrementValuesMap = [[30, 'm'], [30, 'm'], [1, 'd'], [1, 'd'], [1, 'd'], [1, 'd'], [1, 'M']];

const getMappedPeriodItem = (PERIOD, propertyMap): number | moment | [number, string] => {
    const index = findIndex(x => periods[x].constant === PERIOD, Object.keys(periods));
    return index > -1 ? propertyMap[index] : first(propertyMap);
};

const _padHistoryData = (history, period) => {
    if (history.length === 0) {
        return history;
    }

    const firstItemDate = first(history).date;
    const lastItemDate = last(history).date;
    const { constant: PERIOD } = period;
    const minLengthMap = [
        minDayLength,
        minDayLength,
        minWeekLength,
        minWeekLength,
        getMinMonthLength(lastItemDate),
        getMinMonthLength(lastItemDate),
        minYearLength
    ];

    const firstPeriodValuesMap = [
        moment(firstItemDate).hour(),
        moment(firstItemDate).hour(),
        (moment(firstItemDate).weekday() + 1) % 7,
        (moment(firstItemDate).weekday() + 1) % 7,
        moment(firstItemDate).date() - 1,
        moment(firstItemDate).date() - 1,
        moment(firstItemDate).month()
    ];

    const startOfPeriodValuesMap = [
        moment(firstItemDate).startOf('day'),
        moment(firstItemDate).startOf('day'),
        moment(firstItemDate)
            .startOf('week')
            .subtract(1, 'day'),
        moment(firstItemDate)
            .startOf('week')
            .subtract(1, 'day'),
        moment(firstItemDate).startOf('month'),
        moment(firstItemDate).startOf('month'),
        moment(firstItemDate).startOf('year')
    ];

    const minLength = getMappedPeriodItem(PERIOD, minLengthMap);
    const incrementValues = getMappedPeriodItem(PERIOD, incrementValuesMap);
    const firstPeriodValue = getMappedPeriodItem(PERIOD, firstPeriodValuesMap);
    const startOfPeriodValue = getMappedPeriodItem(PERIOD, startOfPeriodValuesMap);
    const defaultValue = 0;
    let granularity = 'minutes';

    if (Array.isArray(incrementValues)) {
        granularity = incrementValues[1] === 'd' ? 'days' : incrementValues[1] === 'M' ? 'months' : 'minutes';
    }

    let finalHistoryData = history;

    if (typeof firstPeriodValue === 'number' && firstPeriodValue !== 0) {
        const iterations =
            PERIOD === periods.TODAY.constant || PERIOD === periods.YESTERDAY.constant
                ? firstPeriodValue * 2
                : firstPeriodValue;

        finalHistoryData = [
            ...range(0, iterations).reduce((all, item, index) => {
                if (index === 0) {
                    return [
                        {
                            date: startOfPeriodValue.valueOf(),
                            value: defaultValue
                        }
                    ];
                }

                return [
                    ...all,
                    {
                        date: moment(last(all).date)
                            .add(...incrementValues)
                            .valueOf(),
                        value: defaultValue
                    }
                ];
            }, []),
            ...history
        ];
    }

    if (typeof minLength === 'number' && finalHistoryData.length < minLength) {
        const fillDateGaps = finalHistoryData.reduce((all, item, index) => {
            if (index === 0) {
                return [item];
            }

            const lastDate = moment(last(all).date);
            const currentDate = moment(item.date);
            const diff = currentDate.diff(lastDate, granularity);
            const timeStep: number = first(incrementValues);
            const diffTotal = diff / timeStep;

            if (diff > timeStep) {
                const fillerItems = range(0, diffTotal - 1).reduce((agg, item, index) => {
                    if (index === 0) {
                        return [
                            {
                                date: lastDate.add(...incrementValues).valueOf(),
                                value: defaultValue
                            }
                        ];
                    }

                    return [
                        ...agg,
                        {
                            date: moment(last(agg).date)
                                .add(...incrementValues)
                                .valueOf(),
                            value: defaultValue
                        }
                    ];
                }, []);
                return [...all, ...fillerItems, item];
            }

            return [...all, item];
        }, []);

        const missingEntries = minLength - fillDateGaps.length;
        return range(0, missingEntries).reduce(all => {
            return [
                ...all,
                {
                    date: moment(last(all).date)
                        .add(...incrementValues)
                        .valueOf(),
                    value: defaultValue
                }
            ];
        }, fillDateGaps);
    }

    return finalHistoryData;
};

const _mapGraphData = (graphData, selectedPeriod, timezone) => {
    if (graphData.length) {
        const history = _padHistoryData(graphData, selectedPeriod);
        return _injectXLabels(history, selectedPeriod.resolution, timezone);
    }

    return graphData;
};

/*****************
 *   Component   *
 *****************/
type Props = {
    animDuration: number,
    graphData: Array<{
        date: number,
        value: string | number
    }>,
    graphStyles: {
        axisColour: string,
        barColours: Array<*>,
        barColourMode: string,
        fontColour: string,
        fontFamily: string,
        fontSize: string
    },
    intl: $npm$ReactIntl$IntlShape,
    noData: boolean,
    period: Object,
    timezone: string,
    xDomain: string,
    yDomain: string
};

const TemperatureGraphComponent = ({
    animDuration = 500,
    graphData,
    graphStyles = {
        axisColour: 'rgb(47, 68, 78)',
        barColours: getRangeColourStrings(32, 0).reverse(),
        barColourMode: YDOMAIN,
        fontColour: 'rgb(47, 68, 78)',
        fontFamily: 'Bariol, Arial',
        fontSize: '1em'
    },
    timezone,
    intl: { formatMessage },
    noData,
    period,
    xDomain = 'date',
    yDomain = 'value'
}: Props) => (
    <BarGraph
        animDuration={animDuration}
        graphData={_mapGraphData(graphData, period, timezone)}
        graphStyles={graphStyles}
        noData={noData}
        noDataMessage={formatMessage(messages.noDataMessage)}
        xDomain={xDomain}
        yDomain={yDomain}
    />
);

export default injectIntl(TemperatureGraphComponent);



// WEBPACK FOOTER //
// ./src/components/widgets/temperature-graph/index.js